import { ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { ReportTemplateContent } from "../../db/schema";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Button from "../common/Button";
import PreferencesForm from "../Settings/PreferencesForm";
import ReportTemplateManager, {
  ReportTemplateManagerHandle,
} from "../Settings/ReportTemplates/ReportTemplateManager";

const AgencyPreferences = () => {
  const { agency } = useAgencyAuth();
  const [isPushing, setIsPushing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const templateRef = useRef<ReportTemplateManagerHandle>(null);
  const initialTemplateContent = useRef<string | null>(null);
  const [templateDirty, setTemplateDirty] = useState(false);

  const handleTemplateContentChange = useCallback(
    (content: ReportTemplateContent) => {
      if (initialTemplateContent.current === null) {
        initialTemplateContent.current = JSON.stringify(content);
      }
      setTemplateDirty(
        JSON.stringify(content) !== initialTemplateContent.current
      );
    },
    []
  );

  const handlePushPreferences = async () => {
    setIsPushing(true);
    try {
      const { data } = await axios.post("/api/agency/push-preferences");
      toast.success(data.message);
    } catch (error) {
      toast.error(
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to push preferences"
      );
    } finally {
      setIsPushing(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      <h2 className="text-2xl font-semibold text-base-content mb-2">
        Agency Report Preferences
      </h2>
      <p className="text-base-content/60 mb-6">
        These preferences will be applied as defaults for all officers in your
        agency. Officers can override these settings in their individual
        preferences.
      </p>
      <PreferencesForm
        entityName={agency?.name || undefined}
        entityId={agency?.id}
        entityType="agency"
        fetchEndpoint="/api/agency/report-preferences"
        saveEndpoint="/api/agency/report-preferences"
        customButtonText="Save Agency Preferences"
        externalDirty={templateDirty}
        onAfterSave={async () => {
          await (templateRef.current?.save() ?? Promise.resolve());
          initialTemplateContent.current = null;
          setTemplateDirty(false);
        }}
      >
        <ReportTemplateManager
          ref={templateRef}
          onContentChange={handleTemplateContentChange}
          apiBasePath="/api/agency/report-templates"
        />
      </PreferencesForm>

      <Button
        variant="secondary"
        className="w-full mt-2"
        onClick={() => setShowConfirmModal(true)}
        disabled={isPushing}
      >
        <ArrowRightStartOnRectangleIcon className="size-5" />
        Push Preferences to All Officers
      </Button>

      <dialog
        id="modal-push-preferences"
        className={`modal ${showConfirmModal ? "modal-open" : ""}`}
      >
        <div className="modal-box max-w-96">
          <h3 className="font-bold text-lg mt-2">Confirm Push Preferences</h3>
          <p className="py-4 text-gray-600 dark:text-base-content/70">
            Are you sure you want to push all agency preferences to all officers?
            This will override their individual preferences and cannot be
            undone.
          </p>
          <div className="modal-action mt-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={isPushing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePushPreferences}
              disabled={isPushing}
            >
              {isPushing ? "Pushing..." : "Confirm"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowConfirmModal(false)}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default AgencyPreferences;
